import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import { formContextInitialValue, useFormContext } from "@starter/Framework/Components/FormContext/FormContext";
import {
    getFirstInputInError,
    getFormById,
    getInputById,
    getInputs,
    hasErrorInForm,
} from "@starter/Framework/Store/Starter/Components/Form/FormSelectors";
import { InputModel } from "@starter/Framework/Store/Starter/Components/Form/FormTypes";
import destroyForm from "@starter/Framework/Store/Starter/Components/Form/Handlers/DestroyForm";
import emptyForm from "@starter/Framework/Store/Starter/Components/Form/Handlers/EmptyForm";
import setForm from "@starter/Framework/Store/Starter/Components/Form/Handlers/SetForm";
import setInput from "@starter/Framework/Store/Starter/Components/Form/Handlers/SetInput";
import { ReactNode, useEffect, useState } from "react";

const useDispatchSetInput = () => {
    const dispatch = useAppDispatch();

    return (formId: string, input: InputModel) => dispatch(setInput({ formId, input }));
};

export const useCurrentForm = () => {
    const form = useFormContext();
    const dispatchSetInput = useDispatchSetInput();

    const setForm = (input: InputModel) => {
        dispatchSetInput(form.id, input);
    };

    return { form, setForm };
};

export const useEmptyForm = (formId: string) => {
    const dispatch = useAppDispatch();

    return () => dispatch(emptyForm({ formId }));
};

export const useGetFirstInputInError = (formId: string) => {
    return useAppSelector(state => getFirstInputInError(state, formId));
};

export const useGetFormById = (id: string) => {
    return useAppSelector(state => getFormById(state, id));
};

export const useGetInputs = (formId: string) => {
    return useAppSelector(state => getInputs(state, formId));
};

export const useGetInputById = (inputId: string, formId: string) => {
    return useAppSelector(state => getInputById(state, inputId, formId));
};

export const useHasErrorInForm = (formId: string) => {
    return useAppSelector(state => hasErrorInForm(state, formId));
};

export const useInitForm = () => {
    const dispatch = useAppDispatch();
    const [formId] = useState(`form-${uniqueId()}`);
    const emptyForm = useEmptyForm(formId);
    const form = useGetFormById(formId) || formContextInitialValue;

    useEffect(() => {
        dispatch(setForm({ formId }));

        return () => {
            dispatch(destroyForm({ formId }));
        };
    }, []);

    return {
        form,
        emptyForm,
    };
};

export const useValidateForm = (id: string | number, error: ReactNode) => {
    const { setForm } = useCurrentForm();
    const inputId = String(id);

    useEffect(() => {
        setForm({
            id: inputId,
            error,
        });
    }, [error]);
};
